import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import thoughtfulManagerImg from "@/assets/manager-thoughtful-woman.png";

interface ScenarioStepFullscreenProps {
  employeePersona: { nom: string; genre?: string };
  /** Optional — when omitted, only the manager thought + facts + reveal are shown. */
  employeeThought?: string;
  managerThought: string;
  revealLabel?: string;
  revealTitle?: string;
  ctaLabel: string;
  /** Localized label shown above the employee thought bubble. */
  employeeLabel?: string;
  /** Localized label shown above the manager thought bubble. */
  managerLabel?: string;
  /** Optional bullet facts displayed between the top thought bubble and the reveal box. */
  facts?: string[];
  /** Optional illustration displayed centered at the top of the overlay. */
  illustrationUrl?: string;
  onContinue: () => void;
  className?: string;
}

/**
 * Comic-style thought bubble (cloud shape) with trailing dots
 * pointing toward `tail` side ("left" | "right").
 */
function ThoughtCloud({
  children,
  label,
  tail,
  accent,
}: {
  children: React.ReactNode;
  label: string;
  tail: "left" | "right";
  accent: string;
}) {
  return (
    <div
      className={cn(
        "relative max-w-md",
        tail === "left" ? "animate-[slide-in-left_500ms_ease-out]" : "animate-[slide-in-right_500ms_ease-out]",
      )}
    >
      {/* Main cloud */}
      <div
        className="relative bg-white rounded-[2.5rem] px-7 py-6 shadow-2xl border-4 border-white"
        style={{
          boxShadow:
            "0 25px 50px -12px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.1)",
        }}
      >
        {/* Bumps around the cloud */}
        <span className="absolute -top-3 left-10 w-8 h-8 rounded-full bg-white border-4 border-white" />
        <span className="absolute -top-2 left-24 w-6 h-6 rounded-full bg-white border-4 border-white" />
        <span className="absolute -top-4 right-12 w-10 h-10 rounded-full bg-white border-4 border-white" />
        <span className="absolute -top-1 right-28 w-5 h-5 rounded-full bg-white border-4 border-white" />
        <span
          className={cn(
            "absolute -bottom-3 w-7 h-7 rounded-full bg-white border-4 border-white",
            tail === "left" ? "left-12" : "right-12",
          )}
        />

        <div
          className="text-[11px] font-bold uppercase tracking-[0.24em] mb-2"
          style={{ color: accent }}
        >
          {label}
        </div>
        <p className="text-[16px] italic leading-relaxed text-slate-800">
          « {children} »
        </p>
      </div>

      {/* Trailing dots toward the silhouette */}
      <div
        className={cn(
          "absolute flex flex-col items-center gap-1.5",
          tail === "left" ? "left-6 -bottom-10" : "right-6 -bottom-10",
        )}
      >
        <span className="w-4 h-4 rounded-full bg-white/90 shadow-lg" />
        <span className="w-2.5 h-2.5 rounded-full bg-white/80 shadow-md ml-3" />
        <span className="w-1.5 h-1.5 rounded-full bg-white/70 shadow ml-1" />
      </div>
    </div>
  );
}

function Silhouette({ variant }: { variant: "a" | "b" }) {
  // Two slightly different silhouettes (head + shoulders)
  return (
    <svg
      viewBox="0 0 200 240"
      className="w-44 md:w-56 h-auto drop-shadow-[0_20px_40px_rgba(0,0,0,0.5)]"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={`sil-${variant}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1a1530" />
          <stop offset="100%" stopColor="#06040d" />
        </linearGradient>
      </defs>
      {variant === "a" ? (
        <>
          {/* Head */}
          <ellipse cx="100" cy="70" rx="42" ry="48" fill={`url(#sil-${variant})`} />
          {/* Hair hint */}
          <path d="M58 68 Q60 24 100 22 Q140 24 142 68 Q140 50 100 48 Q60 50 58 68 Z" fill="#06040d" />
          {/* Shoulders */}
          <path d="M20 240 Q22 150 100 138 Q178 150 180 240 Z" fill={`url(#sil-${variant})`} />
        </>
      ) : (
        <>
          <ellipse cx="100" cy="72" rx="44" ry="50" fill={`url(#sil-${variant})`} />
          {/* Short hair */}
          <path d="M56 70 Q58 28 100 26 Q142 28 144 70 Q140 56 100 54 Q60 56 56 70 Z" fill="#06040d" />
          <path d="M14 240 Q18 148 100 136 Q182 148 186 240 Z" fill={`url(#sil-${variant})`} />
        </>
      )}
    </svg>
  );
}

export default function ScenarioStepFullscreen({
  employeePersona,
  employeeThought,
  managerThought,
  revealLabel,
  revealTitle,
  ctaLabel,
  employeeLabel,
  managerLabel,
  facts,
  illustrationUrl,
  onContinue,
  className,
}: ScenarioStepFullscreenProps) {
  const resolvedEmployeeLabel =
    employeeLabel ?? `PENSÉE DE ${employeePersona.nom.toUpperCase()}`;
  const resolvedManagerLabel = managerLabel ?? "VOTRE GESTIONNAIRE";
  const hasEmployeeThought = !!employeeThought;
  const hasReveal = !!(revealLabel || revealTitle);

  return (
    <div
      role="dialog"
      aria-modal="true"
      className={cn(
        "fixed inset-0 z-50 overflow-y-auto animate-fade-in",
        className,
      )}
      style={{
        background:
          "radial-gradient(ellipse at top, #2b1d4d 0%, #160f2e 45%, #08060f 100%)",
      }}
    >
      {/* Soft moonlight glows */}
      <div className="pointer-events-none absolute top-[-10%] left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full bg-[#7C3AED]/25 blur-[140px]" />
      <div className="pointer-events-none absolute bottom-0 left-0 w-[500px] h-[300px] rounded-full bg-[#2DD4BF]/15 blur-[140px]" />
      <div className="pointer-events-none absolute bottom-0 right-0 w-[500px] h-[300px] rounded-full bg-[#FCDFCB]/10 blur-[140px]" />

      {/* Floor light */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/60 to-transparent" />

      <div className="relative min-h-full flex flex-col items-center justify-end gap-10 px-6 py-12 md:py-16 max-w-6xl mx-auto">
        {/* Optional illustration at the top (centered) */}
        {illustrationUrl && (
          <div className="w-full flex justify-center pt-2">
            <img
              src={illustrationUrl}
              alt={`Illustration de ${employeePersona.nom}`}
              className="max-h-64 md:max-h-80 w-auto drop-shadow-[0_25px_50px_rgba(0,0,0,0.55)] rounded-2xl"
              loading="lazy"
            />
          </div>
        )}

        {/* Thought clouds — two side-by-side when both present, otherwise centered manager-only */}
        {hasEmployeeThought ? (
          <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-end">
            <div className="flex justify-center md:justify-end">
              <ThoughtCloud label={resolvedEmployeeLabel} tail="right" accent="#7C3AED">
                {employeeThought}
              </ThoughtCloud>
            </div>
            <div className="flex justify-center md:justify-start">
              <ThoughtCloud label={resolvedManagerLabel} tail="left" accent="#0d9488">
                {managerThought}
              </ThoughtCloud>
            </div>
          </div>
        ) : (
          <div className="w-full flex flex-col items-center gap-6">
            <ThoughtCloud label={resolvedManagerLabel} tail="left" accent="#0d9488">
              {managerThought}
            </ThoughtCloud>
            <img
              src={thoughtfulManagerImg}
              alt="Gestionnaire songeuse"
              width={640}
              height={896}
              loading="lazy"
              className="hidden md:block w-40 lg:w-56 h-auto drop-shadow-[0_25px_50px_rgba(0,0,0,0.55)] pointer-events-none select-none mt-2"
            />
          </div>
        )}

        {/* Optional facts list, between the top thought bubble(s) and the reveal box */}
        {facts && facts.length > 0 && (
          <ul className="w-full max-w-2xl space-y-2 text-white/85 text-[15px] md:text-base leading-relaxed">
            {facts.map((fact, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-2 inline-block w-2 h-2 rounded-full bg-gradient-to-br from-[#A78BFA] to-[#2DD4BF] flex-shrink-0" />
                <span>{fact}</span>
              </li>
            ))}
          </ul>
        )}

        {/* Reveal box (rendered when revealLabel or revealTitle is provided) */}
        {hasReveal && (
          <div className="w-full max-w-3xl rounded-2xl border border-white/15 bg-white/[0.06] backdrop-blur-md px-6 py-5 shadow-2xl">
            {revealLabel && (
              <div className="text-[11px] font-bold uppercase tracking-[0.32em] text-[#FCDFCB] mb-2">
                {revealLabel}
              </div>
            )}
            {revealTitle && (
              <div className="text-lg md:text-xl font-semibold text-white leading-snug">
                {revealTitle}
              </div>
            )}
          </div>
        )}

        {/* Silhouettes only when the dual-thought layout is used (scenario 1) */}
        {hasEmployeeThought && !illustrationUrl && (
          <div className="flex items-end justify-center gap-8 md:gap-20">
            <Silhouette variant="a" />
            <Silhouette variant="b" />
          </div>
        )}

        {/* CTA */}
        <div className="pb-4">
          <Button variant="cta" size="lg" onClick={onContinue}>
            {ctaLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
